import React, { useContext } from 'react';

import DOMPurify from 'isomorphic-dompurify';
import Head from 'next/head';
import { useRouter } from 'next/router';

import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import UserContext from '@components/lpc/Context/User/UserContext';
import { getFullRoute } from '@services/http/Route';
import ObjectService from '@services/utils/ObjectService';

const SCRIPT_ID = process.env.CHATBOT_SCRIPT_ID;

type HeadProps = {
    showChatBase?: boolean;
    title?: string;
    description?: string;
    ogTitle?: string;
    ogDescription?: string;
    ogUrl?: string;
    ogImage?: string;
    ogImageWidth?: string;
    ogImageHeight?: string;
    ogSiteName?: string;
    isPotPage?: boolean;
    metaTwitterCard?: string;
    potCategory?: string;
};

const CustomHead = ({
    title,
    description,
    ogTitle,
    ogDescription,
    ogUrl,
    ogImage,
    ogImageWidth,
    ogImageHeight,
    ogSiteName,
    isPotPage,
    showChatBase,
    metaTwitterCard,
    potCategory,
}: HeadProps) => {
    const router = useRouter();
    const userContext = useContext(UserContext);
    const { t } = useTranslateLpc(['common'], { useSuspense: false });
    const { userAccount } = userContext;

    const metaTitle = title || t('meta-tag.default-title');
    const metaDescription = potCategory === 'SOLIDARITY_POOL' ? '' : description || t('meta-tag.default-description');
    const metaNoIndex = t('meta-tag.default-no-index');

    const metaOgTitle = ogTitle || t('meta-tag.default-title');
    const metaOgDescription = ogDescription || t('meta-tag.default-description');
    const metaOgSiteName = ogSiteName || t('meta-tag.default-site-name');
    const metaOgUrl = ogUrl || router.pathname;
    const metaOgImage = ogImage || getFullRoute('/front-static/images/opengraph-lpc.png');
    const metaOgImageWidth = ogImageWidth || '1200';
    const metaOgImageHeight = ogImageHeight || '630';

    return (
        <>
            <Head>
                <meta charSet={'utf-8'} />
                <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
                <meta
                    content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
                    name="viewport"
                />

                <title>{metaTitle}</title>
                <meta name="description" content={metaDescription} />
                {isPotPage && <meta name="robots" content={metaNoIndex} />}

                <meta property="og:type" content={'website'} />
                <meta property="og:title" content={metaOgTitle} />
                <meta property="og:description" content={metaOgDescription} />
                <meta property="og:site_name" content={metaOgSiteName} />
                <meta property="og:url" content={metaOgUrl} />
                <meta property="og:image" content={metaOgImage} />
                <meta property="og:image:width" content={metaOgImageWidth} />
                <meta property="og:image:height" content={metaOgImageHeight} />
                <link rel="image_src" href={metaOgImage} />

                <meta name="twitter:card" content={metaTwitterCard || ''} />
                <meta name="twitter:title" content={metaOgTitle} />
                <meta name="twitter:description" content={metaOgDescription} />
                <meta name="twitter:image" content={metaOgImage} />
                <meta property="twitter:image:width" content={metaOgImageWidth} />
                <meta property="twitter:image:height" content={metaOgImageHeight} />

                <meta property="fb:app_id" content={process.env.FACEBOOK_APP_ID} />
                {showChatBase && (
                    <>
                        {ObjectService.hasContent(userAccount) && (
                            <script
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(`
                                        window.chatbaseUserConfig = {
                                            user_id: "${userAccount.userID}",
                                            user_hash: "${userAccount.userHash}",        
                                            user_metadata: {
                                                firstName: "${userAccount.firstName}",
                                                deviceChannel: "${userAccount.deviceChannel}"
                                            }
                                        };
                                    `),
                                }}
                            ></script>
                        )}
                        {ObjectService.hasContent(userAccount) && (
                            <script
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(`
                                    window.chatbaseConfig = {
                                        chatbotId: "${SCRIPT_ID}",
                                        userId: "${userAccount.userID}",
                                        userHash: "${userAccount.userHash}"
                                    };
                                `),
                                }}
                            ></script>
                        )}
                        <script
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(
                                    `(function(){if(!window.chatbase||window.chatbase("getState")!=="initialized"){window.chatbase=(...arguments)=>{if(!window.chatbase.q){window.chatbase.q=[]}window.chatbase.q.push(arguments)};window.chatbase=new Proxy(window.chatbase,{get(target,prop){if(prop==="q"){return target.q}return(...args)=>target(prop,...args)}})}const onLoad=function(){const script=document.createElement("script");script.src="https://www.chatbase.co/embed.min.js";script.id="${SCRIPT_ID}";script.domain="www.chatbase.co";document.body.appendChild(script)};if(document.readyState==="complete"){onLoad()}else{window.addEventListener("load",onLoad)}})();`,
                                ),
                            }}
                        ></script>
                    </>
                )}
            </Head>
        </>
    );
};

export default CustomHead;
